import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Tag, Loader, Empty } from "@/components/kit";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { GraduationCap, Award, Clock, Users, Star, TrendingUp, CheckCircle2, Lock } from "lucide-react";

export default function Skills() {
  const { user } = useAuth();
  const [courses, setCourses] = useState(null);
  const [paths, setPaths] = useState(null);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data.courses)).catch(() => setCourses(false));
    api.get("/career-paths").then((r) => setPaths(r.data.career_paths)).catch(() => {});
  }, []);

  const enrol = async (id) => {
    try { await api.post("/courses/enrol", { course_id: id }); toast.success("Enrolled — added to your training record"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not enrol"); }
  };

  return (
    <div className="fade-up">
      <PageHeader overline="Training & professional development" title="Krida Skills"
        subtitle="Courses, certifications and configurable career pathways for the sports workforce." testid="krida-skills-page" />

      <Tabs defaultValue="courses">
        <TabsList className="bg-krida-surface border border-white/10 mb-6">
          <TabsTrigger value="courses" data-testid="tab-courses" className="data-[state=active]:bg-krida-crimson data-[state=active]:text-white gap-2"><GraduationCap className="w-4 h-4" /> Courses</TabsTrigger>
          <TabsTrigger value="pathways" data-testid="tab-pathways" className="data-[state=active]:bg-krida-crimson data-[state=active]:text-white gap-2"><TrendingUp className="w-4 h-4" /> Career pathways</TabsTrigger>
        </TabsList>

        <TabsContent value="courses">
          {courses === null ? <Loader /> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {courses.map((c) => (
                <div key={c.id} className="krida-card p-5 flex flex-col" data-testid={`course-${c.id}`}>
                  <div className="flex items-start justify-between"><Tag tone="cobalt">{c.category}</Tag><span className="text-xs text-slate-500 flex items-center gap-1"><Star className="w-3 h-3 text-krida-amber" /> {c.rating}</span></div>
                  <h3 className="font-heading text-lg font-bold mt-3">{c.title}</h3>
                  <div className="text-xs text-slate-500 mt-1">{c.provider}</div>
                  <div className="flex gap-3 mt-3 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {c.duration_hours}h</span>
                    <span className="flex items-center gap-1"><Award className="w-3.5 h-3.5" /> {c.modules} modules</span>
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {c.enrolled}</span>
                  </div>
                  {c.certificate && <div className="text-xs text-krida-emerald flex items-center gap-1 mt-2"><CheckCircle2 className="w-3.5 h-3.5" /> Certificate on completion</div>}
                  {user?.role === "professional" && (
                    <button onClick={() => enrol(c.id)} data-testid={`enrol-btn-${c.id}`} className="mt-4 w-full py-2.5 rounded-xl bg-krida-crimson hover:bg-krida-crimsonDark font-semibold text-sm transition-colors">Enrol now</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="pathways">
          {paths === null ? <Loader /> : paths.length === 0 ? <Empty /> : (
            <div className="space-y-6">
              {paths.map((p) => (
                <div key={p.id} className="krida-card p-6" data-testid={`pathway-${p.role}`}>
                  <h3 className="font-heading text-xl font-bold mb-1">{p.role} · {p.sport}</h3>
                  <p className="text-sm text-slate-500 mb-5">Progression ladder with requirements per level.</p>
                  <div className="flex flex-col sm:flex-row gap-3 overflow-x-auto pb-2">
                    {p.levels.map((lvl, i) => (
                      <div key={i} className="flex items-center gap-3 flex-shrink-0">
                        <div className="w-52 rounded-xl border border-white/10 bg-black/20 p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-heading font-bold">{lvl.level}</span>
                            {i === 2 ? <Tag tone="emerald">Current</Tag> : i < 2 ? <CheckCircle2 className="w-4 h-4 text-krida-emerald" /> : <Lock className="w-4 h-4 text-slate-600" />}
                          </div>
                          <div className="space-y-1 text-xs text-slate-400">
                            <div>Experience: {lvl.experience}y</div>
                            {lvl.games > 0 && <div>Games: {lvl.games}</div>}
                            <div>Courses: {lvl.courses}</div>
                            <div className="text-slate-500 truncate">{lvl.certifications.join(", ")}</div>
                          </div>
                        </div>
                        {i < p.levels.length - 1 && <TrendingUp className="w-5 h-5 text-krida-crimson rotate-90 sm:rotate-0 flex-shrink-0" />}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
